/**
 * Service de Gestion des Commentaires de Modules
 * 
 * Permet d'ajouter, modifier et supprimer des commentaires pour chaque module
 * Sauvegarde persistante dans localStorage
 */

import { useState, useEffect } from 'react';

export interface ModuleComment {
  id: string;
  moduleId: string;
  text: string;
  timestamp: number;
  author?: string;
}

export interface ModuleComments {
  [moduleId: string]: ModuleComment[];
}

class ModuleCommentsService {
  private comments: ModuleComments = {};
  private listeners: Set<(comments: ModuleComments) => void> = new Set();
  private readonly STORAGE_KEY = 'module-comments';

  constructor() {
    this.loadComments();
  }

  /**
   * Ajouter un commentaire à un module
   */
  addComment(moduleId: string, text: string, author?: string): string {
    const commentId = this.generateId();
    const comment: ModuleComment = {
      id: commentId,
      moduleId,
      text: text.trim(),
      timestamp: Date.now(),
      author
    };

    if (!this.comments[moduleId]) {
      this.comments[moduleId] = [];
    }

    this.comments[moduleId].push(comment);
    this.saveComments();
    this.notifyListeners();

    console.log(`💬 Commentaire ajouté pour le module ${moduleId}`);
    return commentId;
  }

  /**
   * Modifier un commentaire existant
   */
  updateComment(moduleId: string, commentId: string, newText: string): boolean {
    const moduleComments = this.comments[moduleId];
    if (!moduleComments) {
      return false;
    }

    const commentIndex = moduleComments.findIndex(c => c.id === commentId);
    if (commentIndex === -1) {
      return false;
    }

    moduleComments[commentIndex] = {
      ...moduleComments[commentIndex],
      text: newText.trim(),
      timestamp: Date.now() // Mettre à jour le timestamp lors de la modification
    };

    this.saveComments();
    this.notifyListeners();

    console.log(`✏️ Commentaire ${commentId} modifié pour le module ${moduleId}`);
    return true;
  }

  /**
   * Supprimer un commentaire
   */
  deleteComment(moduleId: string, commentId: string): boolean {
    const moduleComments = this.comments[moduleId];
    if (!moduleComments) {
      return false;
    }

    const initialLength = moduleComments.length;
    this.comments[moduleId] = moduleComments.filter(c => c.id !== commentId);

    if (this.comments[moduleId].length === initialLength) {
      return false; // Aucun commentaire supprimé
    }

    // Supprimer le tableau s'il est vide
    if (this.comments[moduleId].length === 0) {
      delete this.comments[moduleId];
    }

    this.saveComments();
    this.notifyListeners();

    console.log(`🗑️ Commentaire ${commentId} supprimé du module ${moduleId}`);
    return true;
  }

  /**
   * Obtenir tous les commentaires d'un module
   */
  getModuleComments(moduleId: string): ModuleComment[] {
    return this.comments[moduleId] || [];
  }

  /**
   * Obtenir tous les commentaires de tous les modules
   */
  getAllComments(): ModuleComments {
    return { ...this.comments };
  }

  /**
   * Obtenir le nombre de commentaires pour un module
   */
  getCommentCount(moduleId: string): number {
    return this.comments[moduleId]?.length || 0;
  }

  /**
   * Rechercher des commentaires par texte
   */
  searchComments(searchTerm: string): ModuleComment[] {
    const results: ModuleComment[] = [];
    const term = searchTerm.toLowerCase().trim();

    if (!term) {
      return results;
    }

    Object.values(this.comments).forEach(moduleComments => {
      moduleComments.forEach(comment => {
        if (comment.text.toLowerCase().includes(term) || 
            comment.author?.toLowerCase().includes(term)) {
          results.push(comment);
        }
      });
    });

    // Trier par timestamp décroissant (plus récents en premier)
    return results.sort((a, b) => b.timestamp - a.timestamp);
  }

  /**
   * Obtenir les commentaires récents (dernières 24h)
   */
  getRecentComments(hours: number = 24): ModuleComment[] {
    const cutoffTime = Date.now() - (hours * 60 * 60 * 1000);
    const recentComments: ModuleComment[] = [];

    Object.values(this.comments).forEach(moduleComments => {
      moduleComments.forEach(comment => {
        if (comment.timestamp >= cutoffTime) {
          recentComments.push(comment);
        }
      });
    });

    return recentComments.sort((a, b) => b.timestamp - a.timestamp);
  }

  /**
   * Supprimer tous les commentaires d'un module
   */
  clearModuleComments(moduleId: string): boolean {
    if (!this.comments[moduleId]) {
      return false;
    }

    delete this.comments[moduleId];
    this.saveComments();
    this.notifyListeners();

    console.log(`🧹 Tous les commentaires du module ${moduleId} ont été supprimés`);
    return true;
  }

  /**
   * Exporter tous les commentaires au format JSON
   */
  exportComments(): string {
    const exportData = {
      comments: this.comments,
      exportDate: new Date().toISOString(),
      version: '1.0'
    };

    return JSON.stringify(exportData, null, 2);
  }

  /**
   * Importer des commentaires depuis un JSON
   */
  importComments(jsonData: string): boolean {
    try {
      const data = JSON.parse(jsonData);
      
      if (data.comments && typeof data.comments === 'object') {
        // Valider la structure des données
        const isValid = Object.values(data.comments).every((moduleComments: any) => 
          Array.isArray(moduleComments) && 
          moduleComments.every((comment: any) => 
            comment.id && comment.moduleId && comment.text && comment.timestamp
          )
        );

        if (isValid) {
          this.comments = data.comments;
          this.saveComments();
          this.notifyListeners();
          
          console.log('📥 Commentaires importés avec succès');
          return true;
        }
      }
      
      console.error('❌ Format de données invalide pour l\'importation');
      return false;
      
    } catch (error) {
      console.error('❌ Erreur lors de l\'importation des commentaires:', error);
      return false;
    }
  }

  /**
   * Obtenir les statistiques des commentaires
   */
  getCommentStats(): {
    totalComments: number;
    modulesWithComments: number;
    averageCommentsPerModule: number;
    oldestComment?: Date;
    newestComment?: Date;
  } {
    let totalComments = 0;
    let oldestTimestamp = Infinity;
    let newestTimestamp = 0;

    const modulesWithComments = Object.keys(this.comments).length;

    Object.values(this.comments).forEach(moduleComments => {
      totalComments += moduleComments.length;
      
      moduleComments.forEach(comment => {
        if (comment.timestamp < oldestTimestamp) {
          oldestTimestamp = comment.timestamp;
        }
        if (comment.timestamp > newestTimestamp) {
          newestTimestamp = comment.timestamp;
        }
      });
    });

    return {
      totalComments,
      modulesWithComments,
      averageCommentsPerModule: modulesWithComments > 0 ? totalComments / modulesWithComments : 0,
      oldestComment: oldestTimestamp !== Infinity ? new Date(oldestTimestamp) : undefined,
      newestComment: newestTimestamp > 0 ? new Date(newestTimestamp) : undefined
    };
  }

  /**
   * Générer un ID unique pour les commentaires
   */
  private generateId(): string {
    return `comment_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Sauvegarder les commentaires dans localStorage
   */
  private saveComments(): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.comments));
    } catch (error) {
      console.error('❌ Erreur sauvegarde commentaires:', error);
    }
  }

  /**
   * Charger les commentaires depuis localStorage
   */
  private loadComments(): void {
    try {
      const saved = localStorage.getItem(this.STORAGE_KEY);
      if (saved) {
        this.comments = JSON.parse(saved);
      }
    } catch (error) {
      console.error('❌ Erreur chargement commentaires:', error);
      this.comments = {};
    }
  }

  /**
   * Ajouter un listener pour les changements
   */
  addListener(listener: (comments: ModuleComments) => void): void {
    this.listeners.add(listener);
  }

  /**
   * Supprimer un listener
   */
  removeListener(listener: (comments: ModuleComments) => void): void {
    this.listeners.delete(listener);
  }

  /**
   * Notifier tous les listeners des changements
   */
  private notifyListeners(): void {
    this.listeners.forEach(listener => {
      try {
        listener({ ...this.comments });
      } catch (error) {
        console.error('❌ Erreur notification listener commentaires:', error);
      }
    });
  }
}

// Instance singleton
export const moduleComments = new ModuleCommentsService();

// Hook React pour utiliser les commentaires

export function useModuleComments(moduleId?: string) {
  const [comments, setComments] = useState<ModuleComments>(moduleComments.getAllComments());

  useEffect(() => {
    const listener = (newComments: ModuleComments) => {
      setComments(newComments);
    };

    moduleComments.addListener(listener);
    
    return () => {
      moduleComments.removeListener(listener);
    };
  }, []);

  const moduleSpecificComments = moduleId ? comments[moduleId] || [] : [];

  return {
    allComments: comments,
    moduleComments: moduleSpecificComments,
    addComment: (text: string, author?: string) => 
      moduleId ? moduleComments.addComment(moduleId, text, author) : null,
    updateComment: (commentId: string, newText: string) => 
      moduleId ? moduleComments.updateComment(moduleId, commentId, newText) : false,
    deleteComment: (commentId: string) => 
      moduleId ? moduleComments.deleteComment(moduleId, commentId) : false,
    getCommentCount: (id: string) => moduleComments.getCommentCount(id),
    searchComments: (term: string) => moduleComments.searchComments(term),
    getRecentComments: (hours?: number) => moduleComments.getRecentComments(hours),
    clearModuleComments: (id: string) => moduleComments.clearModuleComments(id),
    getCommentStats: () => moduleComments.getCommentStats(),
    exportComments: () => moduleComments.exportComments(),
    importComments: (jsonData: string) => moduleComments.importComments(jsonData)
  };
}